// Invoice / receipt input modal — extracted from the old inline panel.

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import Modal, { Field, ModalActions, PrimaryButton, SecondaryButton } from './Modal.jsx'
import { useAuth } from '../../../auth/AuthProvider.jsx'
import { getDraftInvoice } from '../../../api/documents.js'

export default function InvoiceModal({ encounterId, onClose, onSubmit, submitting }) {
  const { user } = useAuth()
  // Read the doctor's standard consultation fee from auth context as a
  // fallback when the encounter-side draft can't supply one (rare).
  const standardFeeRaw = user?.doctor_profile?.consultation_fee
  const standardFee = standardFeeRaw != null && standardFeeRaw !== ''
    ? Number(standardFeeRaw)
    : null
  const hasStandardFee = standardFee != null && Number.isFinite(standardFee) && standardFee > 0

  // Auto-fetch the draft invoice payload — every billable artefact
  // attached to this encounter (consultation fee + Rx items + lab
  // tests + imaging studies, all priced from their respective
  // catalogues) rolled into a list. Doctor reviews and adjusts before
  // hitting Generate.
  const draftQ = useQuery({
    queryKey: ['draft-invoice', encounterId],
    queryFn: () => getDraftInvoice(encounterId),
    enabled: Boolean(encounterId),
    staleTime: 30_000,
  })

  const [items, setItems] = useState([
    {
      description: 'Consultation',
      quantity: 1,
      unit_price: hasStandardFee ? standardFee.toFixed(2) : '',
    },
  ])
  const [paid, setPaid] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState('Cash')
  const [reference, setReference] = useState('')
  const [currency, setCurrency] = useState('PGK')
  // Tracks whether the user has touched the items list — once they
  // edit anything we stop auto-replacing on draftQ refetches so we
  // don't blow away their edits.
  const [userTouched, setUserTouched] = useState(false)

  // Replace the rows with the draft payload on first successful fetch.
  // Subsequent refetches are ignored once the doctor has started
  // editing — their state is the source of truth from that point on.
  useEffect(() => {
    if (!draftQ.data || userTouched) return
    const draftItems = (draftQ.data.items || []).map((it) => ({
      description: it.description || '',
      quantity: it.quantity ?? 1,
      unit_price: it.unit_price != null ? Number(it.unit_price).toFixed(2) : '',
    }))
    if (draftItems.length > 0) {
      setItems(draftItems)
    }
    if (draftQ.data.currency) setCurrency(draftQ.data.currency)
  }, [draftQ.data, userTouched])

  const billToOrgName = draftQ.data?.bill_to_org_name || ''
  const patientName = draftQ.data?.patient_name || ''

  // Wrap every mutation so we can tag the items state as user-touched.
  // After the first edit, draftQ refetches won't overwrite the rows.
  const touch = () => { if (!userTouched) setUserTouched(true) }
  const setItem = (i, patch) => {
    touch()
    setItems((arr) => arr.map((row, idx) => (idx === i ? { ...row, ...patch } : row)))
  }
  const addRow = () => {
    touch()
    setItems((arr) => [...arr, { description: '', quantity: 1, unit_price: '' }])
  }
  const removeRow = (i) => {
    touch()
    setItems((arr) => arr.filter((_, idx) => idx !== i))
  }
  const resetToDraft = () => {
    setUserTouched(false)
    if (draftQ.data) {
      const draftItems = (draftQ.data.items || []).map((it) => ({
        description: it.description || '',
        quantity: it.quantity ?? 1,
        unit_price: it.unit_price != null ? Number(it.unit_price).toFixed(2) : '',
      }))
      setItems(draftItems.length > 0 ? draftItems : [{
        description: 'Consultation', quantity: 1,
        unit_price: hasStandardFee ? standardFee.toFixed(2) : '',
      }])
    }
  }

  const subtotal = items.reduce(
    (sum, it) => sum + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0),
    0,
  )

  return (
    <Modal
      title={paid ? 'Receipt' : 'Invoice'}
      subtitle="Add line items and tax. Toggle 'Already paid' to render a receipt."
      onClose={onClose}
      wide
    >
      {/* Auto-fill status — what we drew from the encounter and who
          the bill is addressed to. Helps the doctor verify the auto-
          fill before they hit Generate. */}
      {(draftQ.isLoading || patientName || billToOrgName) && (
        <div className="bg-wantok-50/60 border border-wantok-100 rounded-lg px-3 py-2 mb-3 text-xs text-slate-700">
          {draftQ.isLoading ? (
            <span className="text-slate-500 italic">Pulling consultation, prescriptions, lab and imaging items from the encounter…</span>
          ) : (
            <>
              {patientName && (
                <div>
                  <span className="text-slate-500">Patient:</span>{' '}
                  <span className="font-semibold text-slate-800">{patientName}</span>
                </div>
              )}
              {billToOrgName ? (
                <div>
                  <span className="text-slate-500">Billed to:</span>{' '}
                  <span className="font-semibold text-wantok-800">{billToOrgName}</span>
                  <span className="text-slate-500"> · employer subscription</span>
                </div>
              ) : (
                <div className="text-slate-500">
                  Walk-in patient — invoice issued directly to the patient.
                </div>
              )}
              <div className="mt-1 text-[11px] text-slate-500">
                Items below are auto-filled from the encounter (consultation, prescriptions, lab tests, imaging). Adjust as needed before generating.
                {userTouched && (
                  <button
                    type="button"
                    onClick={resetToDraft}
                    className="ml-2 underline text-wantok-700 font-semibold"
                  >
                    Reset to auto-fill
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}

      <Field label="Currency">
        <select
          value={currency}
          onChange={(e) => setCurrency(e.target.value)}
          className="border-2 border-slate-200 focus:border-wantok-600 focus:outline-none rounded-lg px-3 py-2 w-32 font-semibold"
        >
          <option>PGK</option><option>AUD</option><option>USD</option>
        </select>
      </Field>

      <div className="text-xs font-bold uppercase tracking-wider text-slate-700 mt-3 mb-2">Line items</div>
      <table className="w-full text-sm border-2 border-slate-200 rounded-lg overflow-hidden">
        <thead className="bg-slate-100">
          <tr>
            <th className="text-left px-3 py-2 font-bold text-slate-700">Description</th>
            <th className="text-right px-3 py-2 w-20 font-bold text-slate-700">Qty</th>
            <th className="text-right px-3 py-2 w-32 font-bold text-slate-700">Unit ({currency})</th>
            <th className="text-right px-3 py-2 w-32 font-bold text-slate-700">Amount</th>
            <th className="px-3 py-2 w-10"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, i) => {
            const line = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0)
            return (
              <tr key={i} className="border-t border-slate-200">
                <td className="px-3 py-2">
                  <input
                    type="text"
                    value={it.description}
                    onChange={(e) => setItem(i, { description: e.target.value })}
                    className="w-full border-0 outline-none bg-transparent text-sm font-medium"
                    placeholder="Service or item"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="number" min={0} step="0.01"
                    value={it.quantity}
                    onChange={(e) => setItem(i, { quantity: e.target.value })}
                    className="w-full text-right border-0 outline-none bg-transparent text-sm"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="number" min={0} step="0.01"
                    value={it.unit_price}
                    onChange={(e) => setItem(i, { unit_price: e.target.value })}
                    className="w-full text-right border-0 outline-none bg-transparent text-sm"
                  />
                </td>
                <td className="px-3 py-2 text-right text-slate-800 font-semibold">
                  {line.toFixed(2)}
                </td>
                <td className="px-3 py-2 text-center">
                  {items.length > 1 && (
                    <button onClick={() => removeRow(i)} className="text-rose-500 hover:text-rose-700 text-lg" title="Remove">×</button>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
        <tfoot className="bg-slate-50">
          <tr>
            <td colSpan={3} className="text-right px-3 py-2 font-bold text-slate-700">Subtotal</td>
            <td className="text-right px-3 py-2 font-bold text-slate-900 text-base">{subtotal.toFixed(2)}</td>
            <td></td>
          </tr>
        </tfoot>
      </table>
      <div className="text-xs mt-2">
        <button onClick={addRow} className="text-wantok-700 hover:text-wantok-800 font-semibold">+ Add line</button>
      </div>

      {hasStandardFee ? (
        <div className="text-xs text-slate-600 mt-2">
          Pre-filled from your standard consultation fee
          (<span className="font-semibold">{currency} {standardFee.toFixed(2)}</span>).
          Adjust the unit price above if today's visit was different.
        </div>
      ) : (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mt-2">
          <b>Tip:</b> set your standard consultation fee in{' '}
          <Link to="/settings" className="underline font-semibold">Settings</Link>
          {' '}so future invoices auto-fill. For now, type a unit price into
          the Consultation row above (or leave it as 0).
        </div>
      )}
      <Field label="Already paid?">
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={paid}
            onChange={(e) => setPaid(e.target.checked)}
            className="w-4 h-4 accent-wantok-700"
          />
          <span className="text-sm text-slate-700">Render as a RECEIPT instead of an invoice</span>
        </label>
      </Field>
      {paid && (
        <>
          <Field label="Payment method">
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="border-2 border-slate-200 focus:border-wantok-600 focus:outline-none rounded-lg px-3 py-2 w-44 text-base"
            >
              <option>Cash</option>
              <option>Card</option>
              <option>Mobile money</option>
              <option>Bank transfer</option>
              <option>Insurance</option>
            </select>
          </Field>
          <Field label="Reference (optional)">
            <input
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              className="border-2 border-slate-200 focus:border-wantok-600 focus:outline-none rounded-lg px-3 py-2 text-sm w-full"
              placeholder="Transaction ID / claim no."
            />
          </Field>
        </>
      )}

      <ModalActions>
        <SecondaryButton onClick={onClose} disabled={submitting}>Cancel</SecondaryButton>
        <PrimaryButton
          onClick={() =>
            onSubmit({
              currency,
              // No need to ask the backend to auto-prepend any more — the
              // user-visible row above already carries the fee (pre-filled
              // from their standard or typed manually). What you see is
              // exactly what the PDF will render.
              consultation_fee: false,
              items: items
                .filter((it) => (it.description || '').trim())
                .map((it) => ({
                  description: it.description.trim(),
                  quantity: Number(it.quantity) || 1,
                  unit_price: Number(it.unit_price) || 0,
                })),
              paid,
              payment_method: paid ? paymentMethod : '',
              reference_no: paid ? reference : '',
            })
          }
          disabled={submitting}
        >
          {submitting ? 'Generating…' : (paid ? 'Generate receipt' : 'Generate invoice')}
        </PrimaryButton>
      </ModalActions>
    </Modal>
  )
}
